import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface HelpCenterChat {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageAt: Timestamp | null;
  createdAt: Timestamp | null;
  metadata?: Record<string, unknown>;
  userInfoMap?: Record<string, unknown>;
}

const ADMIN_PARTICIPANT_ID = 'admin';
const CHAT_ID_PREFIX = 'admin_';
const ADMIN_DISPLAY_NAME = 'Admin Chat';
const USERS_COLLECTION = 'Users';
const CHATS_COLLECTION = 'Chats';
const FIRESTORE_IN_LIMIT = 10;

const buildChatId = (currentUserId: string) => `${CHAT_ID_PREFIX}${currentUserId}`;

const fetchUsersByIds = async (userIds: string[]): Promise<Record<string, unknown>> => {
  if (userIds.length === 0) {
    return {};
  }

  const results: Record<string, unknown> = {};

  const chunks: string[][] = [];
  for (let i = 0; i < userIds.length; i += FIRESTORE_IN_LIMIT) {
    chunks.push(userIds.slice(i, i + FIRESTORE_IN_LIMIT));
  }

  for (const chunk of chunks) {
    const usersQuery = query(
      collection(db, USERS_COLLECTION),
      where(documentId(), 'in', chunk),
    );

    const usersSnapshot = await getDocs(usersQuery);
    usersSnapshot.forEach((userDoc) => {
      results[userDoc.id] = userDoc.data();
    });
  }

  return results;
};

export const checkHelpCenterChat = async (
  currentUserId: string,
): Promise<HelpCenterChat | null> => {
  try {
    const chatQuery = query(
      collection(db, CHATS_COLLECTION),
      where('participants', 'array-contains', currentUserId),
    );

    const chatSnapshot = await getDocs(chatQuery);

    for (const docSnapshot of chatSnapshot.docs) {
      const data = docSnapshot.data();
      const participants = Array.isArray(data.participants) ? data.participants : [];

      if (!participants.includes(ADMIN_PARTICIPANT_ID)) {
        continue;
      }

      const userIds = participants.filter((id): id is string => typeof id === 'string' && id !== ADMIN_PARTICIPANT_ID);
      const userInfoMap = await fetchUsersByIds(userIds);

      return {
        id: docSnapshot.id,
        participants,
        lastMessage: data.lastMessage || '',
        lastMessageAt: data.lastMessageAt || null,
        createdAt: data.createdAt || null,
        metadata: {
          ...(typeof data.metadata === 'object' && data.metadata !== null ? data.metadata : {}),
          type: 'admin_support',
          adminDisplayName: ADMIN_DISPLAY_NAME,
        },
        userInfoMap,
      };
    }

    return null;
  } catch (error) {
    console.error('Error checking help center chat:', error);
    throw error;
  }
};

const createHelpCenterChat = async (
  currentUserId: string,
  currentUserName: string,
): Promise<HelpCenterChat> => {
  const chatId = buildChatId(currentUserId);
  const chatRef = doc(db, CHATS_COLLECTION, chatId);
  const snapshot = await getDoc(chatRef);

  if (!snapshot.exists()) {
    await setDoc(chatRef, {
      participants: [currentUserId, ADMIN_PARTICIPANT_ID],
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      metadata: {
        type: 'admin_support',
        adminDisplayName: ADMIN_DISPLAY_NAME,
        createdBy: currentUserId,
        createdByName: currentUserName,
      },
    });
  }

  const userInfoMap = await fetchUsersByIds([currentUserId]);
  const data = (await getDoc(chatRef)).data() || {};

  return {
    id: chatId,
    participants: Array.isArray(data.participants)
      ? (data.participants as string[])
      : [currentUserId, ADMIN_PARTICIPANT_ID],
    lastMessage: data.lastMessage || '',
    lastMessageAt: data.lastMessageAt || null,
    createdAt: data.createdAt || null,
    metadata: {
      ...(typeof data.metadata === 'object' && data.metadata !== null ? data.metadata : {}),
      type: 'admin_support',
      adminDisplayName: ADMIN_DISPLAY_NAME,
    },
    userInfoMap,
  };
};

export const getOrCreateHelpCenterChat = async (
  currentUserId: string,
  currentUserName: string,
): Promise<HelpCenterChat> => {
  try {
    const existingChat = await checkHelpCenterChat(currentUserId);
    if (existingChat) {
      return existingChat;
    }
    return await createHelpCenterChat(currentUserId, currentUserName);
  } catch (error) {
    console.error('Error getting or creating help center chat:', error);
    throw error;
  }
};

